import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  ArrowLeft,
  Search,
  Plus,
  Sparkles,
  LayoutTemplate,
  Edit2,
  Trash2,
  Eye,
  Send,
  X,
  Bot,
  Code,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = "https://emailserverjuly.shahlimon.online";

const getCategoryLabel = (cat) => {
  if (cat === "forSubscriber") return "For Subscriber";
  if (cat === "forRegister") return "For Register";
  return cat || "Uncategorized";
};

const UserEmailTemplate = () => {
  const [user] = useAuthState(auth);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([""]);
  const [availableShortcodes, setAvailableShortcodes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // 9 items per page for a nice 3x3 grid
  const templatesPerPage = 9;

  const fetchTemplates = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(
        `${API_BASE}/user-emails?ownerEmail=${user.email}`,
      );
      setTemplates(res.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const fetchCategories = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(
        `${API_BASE}/user-email-templates?ownerEmail=${user.email}`,
      );
      const existingCategories = [
        ...new Set(res.data.map((u) => u.category).filter(Boolean)),
      ];
      setCategories((prev) => [...new Set([...prev, ...existingCategories])]);

      const customFieldShortcodes = new Set();
      res.data.forEach((contact) => {
        if (contact.customFields && Array.isArray(contact.customFields)) {
          contact.customFields.forEach((f) => {
            if (f.shortcode) customFieldShortcodes.add(f.shortcode);
          });
        }
      });

      const uniqueShortcodes = ["user_name", ...Array.from(customFieldShortcodes)];
      setAvailableShortcodes([...new Set(uniqueShortcodes)]);
    } catch (error) {
      console.error("Failed to fetch categories or shortcodes:", error);
    }
  };

  useEffect(() => {
    if (user?.email) {
      fetchTemplates();
      fetchCategories();
    }
  }, [user]);

  const handleAddOrUpdate = async (template) => {
    const isContentValid =
      template.mode === "ai"
        ? template.aiPrompt?.trim()
        : template.body?.trim();

    if (
      !template.templateName?.trim() ||
      !template.subject?.trim() ||
      !isContentValid ||
      typeof template.day !== "number" ||
      !template.category?.trim()
    ) {
      alert("Please fill all required fields including category.");
      return;
    }

    const payload = { ...template, ownerEmail: user?.email };

    try {
      if (editingTemplate) {
        await axios.put(
          `${API_BASE}/edit-user-email/${editingTemplate._id}`,
          payload,
        );
      } else {
        await axios.post(`${API_BASE}/add-user-email`, payload);
      }
      setEditingTemplate(null);
      setShowModal(false);
      fetchTemplates();
    } catch (error) {
      console.error("Failed to save template:", error);
      alert("Error saving template");
    }
  };

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await axios.delete(
          `${API_BASE}/delete-user-email/${id}?ownerEmail=${user?.email}`,
        );
        fetchTemplates();
      } catch (error) {
        console.error("Failed to delete template:", error);
        alert("Error deleting template");
      }
    }
  };

  const handleToggleStatus = async (template, e) => {
    if (e) e.stopPropagation();
    try {
      await axios.put(`${API_BASE}/edit-user-email/${template._id}`, {
        ...template,
        enabled: !template.enabled,
        ownerEmail: user?.email,
      });
      fetchTemplates();
    } catch (err) {
      alert("Failed to update template status");
    }
  };

  const handleSendNow = async (template, e) => {
    if (e) e.stopPropagation();
    if (
      !window.confirm(
        `Are you sure you want to send "${template.templateName}" to all eligible users now?`,
      )
    ) {
      return;
    }
    try {
      const res = await axios.post(
        `${API_BASE}/api/send-template-now/${template._id}`,
      );
      toast.success(
        res.data.message || `Successfully sent to ${res.data.sentCount} users.`,
      );
      fetchTemplates();
    } catch (error) {
      console.error("Failed to send template:", error);
      toast.error(error.response?.data?.message || "Error sending template");
    }
  };

  const filteredTemplates = templates
    .filter((t) => {
      const matchesSearch = t.templateName
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesCategory =
        categoryFilter === "" || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => (a.day || 0) - (b.day || 0));

  const indexOfLast = currentPage * templatesPerPage;
  const indexOfFirst = indexOfLast - templatesPerPage;
  const currentTemplates = filteredTemplates.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);

  const closeModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setViewTemplate(null);
  };

  return (
    <div className="min-vh-100 bg-dark-bg text-white font-sans overflow-hidden">
      {/* Background Orbs for Premium Effect */}
      <div
        className="position-fixed top-0 start-0 w-100 h-100 overflow-hidden pointer-events-none"
        style={{ zIndex: 0 }}
      >
        <div
          className="position-absolute rounded-circle blur-3xl opacity-20 bg-primary"
          style={{
            width: "40rem",
            height: "40rem",
            top: "-10%",
            left: "-10%",
            filter: "blur(100px)",
          }}
        ></div>
        <div
          className="position-absolute rounded-circle blur-3xl opacity-20"
          style={{
            backgroundColor: "#8b5cf6",
            width: "30rem",
            height: "30rem",
            bottom: "10%",
            right: "-5%",
            filter: "blur(100px)",
          }}
        ></div>
      </div>

      <div
        className="container py-5 position-relative"
        style={{ zIndex: 1, maxWidth: "1400px" }}
      >
        {/* Navigation & Hero */}
        <div className="mb-5">
          <Link
            to="/"
            className="btn btn-outline-light rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2 mb-4 border-opacity-25 hover-bg-light transition-all"
          >
            <ArrowLeft size={16} /> Back to Dashboard
          </Link>

          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-4">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="display-5 fw-bolder mb-2 tracking-tight text-white">
                  AI Email <span className="text-gradient">Sequences</span>
                </h1>
                <p
                  className="text-gray-400 fs-5 mb-0"
                  style={{ maxWidth: "600px" }}
                >
                  Craft, manage, and automate high-converting outreach with the
                  power of artificial intelligence.
                </p>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="d-flex gap-3"
            >
              <Link
                to="/admin/generate-email"
                className="btn btn-gradient-glow rounded-pill px-4 py-2 d-flex align-items-center gap-2 fw-medium border-0 shadow-lg"
              >
                <Sparkles size={18} /> Generate AI Email
              </Link>
              <button
                className="btn btn-glass rounded-pill px-4 py-2 d-flex align-items-center gap-2 fw-medium"
                onClick={() => {
                  setShowModal(true);
                  setEditingTemplate(null);
                  setViewTemplate(null);
                }}
              >
                <Plus size={18} /> Create Manually
              </button>
            </motion.div>
          </div>
        </div>

        {/* Filters Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="glass-panel rounded-4 p-3 mb-5 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3"
        >
          <div
            className="position-relative w-100"
            style={{ maxWidth: "400px" }}
          >
            <Search
              size={18}
              className="position-absolute top-50 translate-middle-y text-gray-400"
              style={{ left: "16px" }}
            />
            <input
              type="text"
              className="form-control bg-dark-surface border-gray-700 text-white rounded-pill ps-5 py-2 shadow-none focus-ring-primary transition-all"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center gap-2 w-100 w-md-auto">
            <span className="text-gray-400 small fw-medium text-nowrap d-none d-md-block">
              Filter by:
            </span>
            <select
              className="form-select bg-dark-surface border-gray-700 text-white rounded-pill py-2 shadow-none focus-ring-primary transition-all w-100"
              style={{ minWidth: "200px" }}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {getCategoryLabel(cat)}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Template Grid */}
        <div className="row g-4 mb-5">
          {currentTemplates.length === 0 ? (
            <div className="col-12 text-center py-5">
              <div
                className="d-inline-flex align-items-center justify-content-center bg-dark-surface rounded-circle mb-3"
                style={{ width: "80px", height: "80px" }}
              >
                <LayoutTemplate size={32} className="text-gray-500" />
              </div>
              <h4 className="text-white">No templates found</h4>
              <p className="text-gray-400">
                Try adjusting your filters or create a new template.
              </p>
            </div>
          ) : (
            currentTemplates.map((template, index) => (
              <motion.div
                key={template._id}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="col-12 col-md-6 col-xl-4"
              >
                <div className="template-card glass-panel rounded-4 h-100 d-flex flex-column position-relative overflow-hidden group">
                  <div className="p-4 flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <span
                        className={`badge rounded-pill px-3 py-2 fw-medium ${template.category === "forSubscriber" ? "bg-primary text-white border border-primary" : template.category === "forRegister" ? "bg-success text-white border border-success" : "bg-secondary bg-opacity-20 text-light border border-secondary border-opacity-25"}`}
                      >
                        {getCategoryLabel(template.category)}
                      </span>

                      <div
                        className="form-check form-switch custom-switch"
                        title={
                          template.enabled
                            ? "Disable Template"
                            : "Enable Template"
                        }
                      >
                        <input
                          className="form-check-input cursor-pointer shadow-none"
                          type="checkbox"
                          checked={template.enabled}
                          onChange={(e) => handleToggleStatus(template, e)}
                        />
                      </div>
                    </div>

                    <h5
                      className="text-white fw-bold mb-2 text-truncate"
                      title={template.templateName}
                    >
                      {template.templateName}
                    </h5>
                    <p
                      className="text-gray-400 small mb-0 text-truncate"
                      title={template.subject}
                    >
                      Subject: {template.subject}
                    </p>
                    <div className="mt-3 d-flex align-items-center gap-2">
                      <span className="text-gray-500 small fw-medium">Format:</span>
                      <span className="badge bg-dark-surface text-gray-400 border border-gray-700">
                        {template.mode === "ai"
                          ? "AI Prompt"
                          : template.contentType === "plainText"
                            ? "Plain Text"
                            : "Rich HTML"}
                      </span>
                    </div>
                  </div>

                  <div className="card-footer-glass p-3 mt-auto border-top border-gray-800 d-flex justify-content-between align-items-center">
                    <div className="text-gray-400 small fw-medium d-flex align-items-center gap-2 flex-wrap">
                      {template.scheduleMode === "scheduled" ||
                      template.scheduledSendAt ? (
                        <>
                          <span className="badge bg-info text-dark px-2 py-1 rounded">
                            <i className="bi bi-clock me-1"></i>
                            {template.day > 0
                              ? "Scheduled + Delay"
                              : "Scheduled"}
                          </span>
                          <span className="text-white">
                            {(template.scheduledSendAt &&
                              new Date(template.scheduledSendAt).toLocaleTimeString(
                                [],
                                { hour: "2-digit", minute: "2-digit" },
                              )) ||
                              "—"}
                          </span>
                        </>
                      ) : template.day === 0 ||
                        template.day === undefined ||
                        template.day === null ? (
                        <span className="text-info fw-bold bg-dark-surface px-2 py-1 rounded">
                          Instant
                        </span>
                      ) : (
                        <>
                          Day{" "}
                          <span className="text-white fw-bold bg-dark-surface px-2 py-1 rounded ms-1">
                            {template.day}
                          </span>
                        </>
                      )}
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-icon btn-glass rounded-circle"
                        onClick={() => {
                          setViewTemplate(template);
                          setShowModal(false);
                          setEditingTemplate(null);
                        }}
                        title="View"
                      >
                        <Eye size={16} className="text-info" />
                      </button>
                      <button
                        className="btn btn-icon btn-glass rounded-circle"
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowModal(true);
                          setViewTemplate(null);
                        }}
                        title="Edit"
                      >
                        <Edit2 size={16} className="text-warning" />
                      </button>
                      <button
                        className="btn btn-icon btn-glass rounded-circle"
                        onClick={(e) => handleDelete(template._id, e)}
                        title="Delete"
                      >
                        <Trash2 size={16} className="text-danger" />
                      </button>
                      <button
                        className="btn btn-icon btn-glass rounded-circle"
                        onClick={(e) => handleSendNow(template, e)}
                        title="Send Now"
                      >
                        <Send size={16} className="text-success" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="d-flex justify-content-center mt-5">
            <ul className="pagination custom-pagination gap-2">
              {[...Array(totalPages)].map((_, i) => (
                <li
                  key={i}
                  className={`page-item ${i + 1 === currentPage ? "active" : ""}`}
                >
                  <button
                    className="page-link rounded-circle d-flex align-items-center justify-content-center fw-bold transition-all"
                    style={{ width: "40px", height: "40px" }}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {(showModal || viewTemplate) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop-custom position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
            style={{ zIndex: 1050 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className={`glass-panel border border-gray-700 rounded-4 overflow-hidden shadow-2xl w-100 mx-auto d-flex flex-column ${viewTemplate ? "max-w-5xl" : "max-w-7xl"}`}
              style={{ maxHeight: "90vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header border-bottom border-gray-800 p-4 d-flex justify-content-between align-items-center bg-dark-bg bg-opacity-50">
                <h4 className="modal-title fw-bold text-white m-0 d-flex align-items-center gap-2">
                  {viewTemplate ? (
                    <Eye className="text-info" />
                  ) : editingTemplate ? (
                    <Edit2 className="text-warning" />
                  ) : (
                    <Plus className="text-primary" />
                  )}
                  {viewTemplate
                    ? "View Template"
                    : editingTemplate
                      ? "Edit Template"
                      : "Create New Template"}
                </h4>
                <button
                  type="button"
                  className="btn btn-icon btn-glass rounded-circle text-gray-400 hover-text-white"
                  onClick={closeModal}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body p-0 overflow-auto custom-scrollbar flex-grow-1">
                {viewTemplate ? (
                  <div className="p-4 p-md-5 bg-dark-bg">
                    <div className="d-flex flex-wrap gap-4 mb-4 pb-4 border-bottom border-gray-800">
                      <div>
                        <div className="text-gray-500 small text-uppercase fw-bold mb-1 tracking-wider">
                          Template Name
                        </div>
                        <div className="text-white fs-5 fw-medium">
                          {viewTemplate.templateName || "-"}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 small text-uppercase fw-bold mb-1 tracking-wider">
                          Category
                        </div>
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-medium ${viewTemplate.category === "forSubscriber" ? "bg-primary text-white border border-primary" : viewTemplate.category === "forRegister" ? "bg-success text-white border border-success" : "bg-secondary bg-opacity-20 text-light border border-secondary border-opacity-25"}`}
                        >
                          {getCategoryLabel(viewTemplate.category)}
                        </span>
                      </div>
                      <div>
                        <div className="text-gray-500 small text-uppercase fw-bold mb-1 tracking-wider">
                          Schedule
                        </div>
                        <div className="text-white fw-medium d-flex align-items-center gap-2 flex-wrap">
                          {viewTemplate.scheduleMode === "scheduled" ||
                          viewTemplate.scheduledSendAt ? (
                            <>
                              <span className="badge bg-info text-dark px-2 py-1 rounded">
                                {viewTemplate.day > 0
                                  ? "Scheduled + Delay"
                                  : "Scheduled"}
                              </span>
                              <span>
                                {(viewTemplate.scheduledSendAt &&
                                  new Date(
                                    viewTemplate.scheduledSendAt,
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })) ||
                                  "—"}
                              </span>
                              {viewTemplate.day > 0 && (
                                <span className="text-gray-500 small">
                                  (after Day {viewTemplate.day})
                                </span>
                              )}
                            </>
                          ) : viewTemplate.day === 0 ? (
                            <span className="text-info fw-bold">Instant</span>
                          ) : (
                            `Day ${viewTemplate.day || "-"}`
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500 small text-uppercase fw-bold mb-1 tracking-wider">
                          Status
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {viewTemplate.enabled ? (
                            <div className="indicator bg-success shadow-glow-success"></div>
                          ) : (
                            <div className="indicator bg-secondary"></div>
                          )}
                          <span className="text-white fw-medium">
                            {viewTemplate.enabled ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="text-gray-500 small text-uppercase fw-bold mb-2 tracking-wider">
                        Subject Line
                      </div>
                      <div className="glass-panel p-3 rounded-3 text-white fw-medium">
                        {viewTemplate.subject || "-"}
                      </div>
                    </div>

                    <div>
                      <div className="text-gray-500 small text-uppercase fw-bold mb-2 tracking-wider mt-4 d-flex justify-content-between align-items-end">
                        <span>{viewTemplate.mode === "ai" ? "AI Generation Prompt" : "Message Content"}</span>
                        <span className="badge bg-dark-surface text-gray-400 border border-gray-700">
                          {viewTemplate.mode === "ai"
                            ? "AI Prompt"
                            : viewTemplate.contentType === "plainText"
                              ? "Plain Text"
                              : "HTML"}
                        </span>
                      </div>

                      {viewTemplate.mode === "ai" ? (
                        <div
                          className="overflow-auto custom-scrollbar text-dark"
                          style={{
                            backgroundColor: "#f8f9fa",
                            padding: "24px",
                            borderRadius: "12px",
                            border: "1px solid #dee2e6",
                            minHeight: "200px",
                            maxHeight: "50vh",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: "1.7",
                            fontSize: "15px",
                          }}
                        >
                          {viewTemplate.aiPrompt || "No prompt provided."}
                        </div>
                      ) : viewTemplate.contentType === "plainText" ? (
                        <div
                          className="overflow-auto custom-scrollbar text-dark"
                          style={{
                            backgroundColor: "#f8f9fa",
                            padding: "24px",
                            borderRadius: "12px",
                            border: "1px solid #dee2e6",
                            minHeight: "200px",
                            maxHeight: "50vh",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: "1.7",
                            fontSize: "15px",
                          }}
                        >
                          {viewTemplate.body}
                        </div>
                      ) : (
                        <div
                          className="overflow-y-auto overflow-x-hidden custom-scrollbar text-dark email-preview-container"
                          style={{
                            backgroundColor: "#f8f9fa",
                            padding: "0",
                            borderRadius: "12px",
                            border: "1px solid #dee2e6",
                            minHeight: "200px",
                            maxHeight: "50vh"
                          }}
                          dangerouslySetInnerHTML={{
                            __html: `<style>
                              .email-preview-container { word-wrap: break-word !important; overflow-wrap: break-word !important; word-break: break-word !important; }
                              .email-preview-container * { max-width: 100% !important; box-sizing: border-box !important; }
                              .email-preview-container img { height: auto !important; }
                            </style>` + DOMPurify.sanitize(viewTemplate.body || ""),
                          }}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 p-md-5">
                    <EmailForm
                      onSubmit={handleAddOrUpdate}
                      onCancel={closeModal}
                      editingTemplate={editingTemplate}
                      categories={categories}
                      availableShortcodes={availableShortcodes}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx="true">{`
        .bg-dark-bg {
          background-color: #020617;
        }
        .bg-dark-surface {
          background-color: rgba(15, 23, 42, 0.6);
        }
        .text-gray-400 {
          color: #94a3b8 !important;
        }
        .text-gray-500 {
          color: #64748b !important;
        }
        .border-gray-800 {
          border-color: #1e293b !important;
        }
        .border-gray-700 {
          border-color: #334155 !important;
        }

        .tracking-tight {
          letter-spacing: -0.025em;
        }
        .tracking-wider {
          letter-spacing: 0.05em;
        }

        .text-gradient {
          background: linear-gradient(
            135deg,
            #a855f7 0%,
            #6366f1 50%,
            #06b6d4 100%
          );
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .glass-panel {
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .btn-glass {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #f8fafc;
          transition: all 0.2s ease;
        }
        .btn-glass:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          color: #fff;
          transform: translateY(-2px);
        }

        .btn-gradient-glow {
          background: linear-gradient(135deg, #6366f1, #a855f7);
          color: white;
          position: relative;
          z-index: 1;
          transition: all 0.3s ease;
        }
        .btn-gradient-glow::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: inherit;
          background: linear-gradient(135deg, #a855f7, #ec4899);
          z-index: -1;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .btn-gradient-glow:hover::before {
          opacity: 1;
        }
        .btn-gradient-glow:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(168, 85, 247, 0.5);
          color: white;
        }

        .focus-ring-primary:focus {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25) !important;
          outline: none;
        }

        .template-card {
          transition: all 0.3s ease;
          border: 1px solid rgba(51, 65, 85, 0.5);
        }
        .template-card:hover {
          transform: translateY(-4px);
          border-color: rgba(99, 102, 241, 0.5);
          box-shadow:
            0 15px 30px -10px rgba(0, 0, 0, 0.5),
            0 0 20px rgba(99, 102, 241, 0.1);
        }

        .card-footer-glass {
          background: rgba(0, 0, 0, 0.2);
        }

        .custom-switch .form-check-input {
          width: 3rem;
          height: 1.5rem;
          background-color: #334155;
          border: none;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba%28255, 255, 255, 0.8%29'/%3e%3c/svg%3e");
        }
        .custom-switch .form-check-input:checked {
          background-color: #10b981;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
        }

        .custom-pagination .page-link {
          background-color: transparent;
          border: 1px solid #334155;
          color: #94a3b8;
        }
        .custom-pagination .page-item.active .page-link {
          background-color: #6366f1;
          border-color: #6366f1;
          color: white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }
        .custom-pagination .page-link:hover:not(.active) {
          background-color: #1e293b;
          border-color: #475569;
          color: white;
        }

        .modal-backdrop-custom {
          background: rgba(2, 6, 23, 0.85);
          backdrop-filter: blur(8px);
        }

        .max-w-3xl {
          max-width: 800px;
        }
        .max-w-5xl {
          max-width: 1140px;
        }
        .max-w-7xl {
          max-width: 1400px;
        }

        .indicator {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .shadow-glow-success {
          box-shadow: 0 0 10px rgba(16, 185, 129, 0.6);
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #334155;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #475569;
        }

        .form-floating-custom {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid #334155;
          border-radius: 0.75rem;
          padding: 0.5rem 1rem;
          transition: all 0.2s;
        }
        .form-floating-custom:focus-within {
          border-color: #6366f1;
          background: rgba(15, 23, 42, 0.8);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
        }
        .form-floating-custom label {
          color: #94a3b8;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }
        .form-floating-custom input,
        .form-floating-custom select,
        .form-floating-custom textarea {
          background: transparent;
          border: none;
          color: white;
          width: 100%;
          outline: none;
        }
        .form-floating-custom input::placeholder,
        .form-floating-custom textarea::placeholder {
          color: #475569;
        }

        .segmented-control {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 100px;
          padding: 4px;
          display: flex;
          border: 1px solid #334155;
        }
        .segmented-control input[type="radio"] {
          display: none;
        }
        .segmented-control label {
          flex: 1;
          text-align: center;
          padding: 8px 16px;
          border-radius: 100px;
          color: #94a3b8;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .segmented-control input[type="radio"]:checked + label {
          background: #6366f1;
          color: white;
          box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
        }

        /* ----- <input type="time"> dark-theme overrides -----
           Native time pickers render with the browser's white-background
           widget by default, which clashes badly with our dark UI. These
           rules force the input background + text/icon colors to match
           the rest of the form, hide the white click-to-open indicator
           on Chrome/Safari, and let our own 🕒 glyph serve as the icon.
        */
        .send-time-input {
          background-color: rgba(15, 23, 42, 0.6) !important;
          color: #f8fafc !important;
          border: 1px solid #334155 !important;
          color-scheme: dark;
        }
        .send-time-input::placeholder,
        .send-time-input::-webkit-input-placeholder {
          color: #64748b;
        }
        /* Hide the native picker button so the input looks like a normal
           text field; our own clock glyph provides the affordance instead. */
        .send-time-input::-webkit-calendar-picker-indicator {
          filter: invert(0.85) hue-rotate(180deg);
          opacity: 0.85;
          cursor: pointer;
        }
        .send-time-input::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
        }
        /* Firefox renders the spin-buttons inside the input. Tone them
           down so they don't show as bright white squares on the side. */
        .send-time-input::-webkit-datetime-edit-ampm-field,
        .send-time-input::-webkit-datetime-edit-hour-field,
        .send-time-input::-webkit-datetime-edit-minute-field {
          color: #f8fafc;
        }
        .send-time-input::-webkit-datetime-edit-text {
          color: #94a3b8;
          padding: 0 2px;
        }
        /* Hover/focus states match the rest of the form */
        .send-time-input:hover {
          border-color: #475569 !important;
        }
        .send-time-input:focus {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25) !important;
          outline: none !important;
        }
      `}</style>
    </div>
  );
};

const EmailForm = ({
  onSubmit,
  onCancel,
  editingTemplate,
  categories,
  availableShortcodes,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [day, setDay] = useState("");
  const [category, setCategory] = useState("forSubscriber");
  const [enabled, setEnabled] = useState(true);
  const [mode, setMode] = useState("html");
  const [contentType, setContentType] = useState("htmlAllowed");
  const [aiPrompt, setAiPrompt] = useState("");
  // Schedule mode: "instant" | "delayed" | "scheduled"
  const [scheduleMode, setScheduleMode] = useState("instant");
  // Time-of-day picker value, e.g. "05:10". Stored separately from the
  // computed ISO `scheduledSendAt` so the user can keep editing the time.
  const [sendTime, setSendTime] = useState("");
  // ISO timestamp that the cron tick actually compares against.
  // Computed at submit via nextOccurrence(sendTime); kept here for traceability.
  // eslint-disable-next-line no-unused-vars
  const [scheduledSendAt, setScheduledSendAt] = useState("");
  const textareaRef = useRef(null);

  // Compute the next future Date that matches an HH:MM time-of-day.
  // e.g. "05:10" right now → if 05:10 today hasn't passed, today; else tomorrow.
  const nextOccurrence = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return null;
    const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (
      !Number.isInteger(h) ||
      !Number.isInteger(min) ||
      h < 0 ||
      h > 23 ||
      min < 0 ||
      min > 59
    ) {
      return null;
    }
    const now = new Date();
    const target = new Date(now);
    target.setHours(h, min, 0, 0);
    if (target.getTime() <= now.getTime()) {
      target.setDate(target.getDate() + 1);
    }
    return target;
  };

  const toTimeString = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Default = now rounded up to the next 10-minute mark, in HH:MM form.
  const getDefaultSendTime = () => {
    const d = new Date(Date.now() + 10 * 60 * 1000);
    return toTimeString(d);
  };

  useEffect(() => {
    if (editingTemplate) {
      setTemplateName(editingTemplate.templateName || "");
      setSubject(editingTemplate.subject || "");
      setBody(editingTemplate.body || "");
      setDay(
        editingTemplate.day !== undefined && editingTemplate.day !== null
          ? editingTemplate.day.toString()
          : "",
      );
      setCategory(editingTemplate.category || "forSubscriber");
      setEnabled(editingTemplate.enabled ?? true);
      setMode(editingTemplate.mode || "html");
      setContentType(editingTemplate.contentType || "htmlAllowed");
      setAiPrompt(editingTemplate.aiPrompt || "");
      // Restore schedule mode: derive from `day` (and presence of scheduledSendAt):
      //   day > 0  → Delayed (with optional scheduledSendAt)
      //   day = 0  → Instant (with optional scheduledSendAt)
      //   day = -1 → Schedule (with scheduledSendAt)
      if (
        typeof editingTemplate.day === "number" &&
        editingTemplate.day > 0
      ) {
        setScheduleMode("delayed");
      } else if (editingTemplate.day === -1) {
        setScheduleMode("scheduled");
      } else {
        // day === 0 (or missing)
        setScheduleMode("instant");
      }
      if (editingTemplate.scheduledSendAt) {
        const d = new Date(editingTemplate.scheduledSendAt);
        if (!Number.isNaN(d.getTime())) {
          setSendTime(toTimeString(d));
        } else {
          setSendTime(getDefaultSendTime());
        }
        setScheduledSendAt(editingTemplate.scheduledSendAt);
      } else {
        // Pre-populate the picker so the user can just confirm "now+10 min"
        setSendTime(getDefaultSendTime());
        setScheduledSendAt("");
      }
    } else {
      setTemplateName("");
      setSubject("");
      setBody("");
      setDay("");
      setCategory("forSubscriber");
      setEnabled(true);
      setMode("html");
      setContentType("htmlAllowed");
      setAiPrompt("");
      setScheduleMode("instant");
      setSendTime("");
      setScheduledSendAt("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTemplate]);

  const insertShortcode = (shortcode) => {
    const code = `{{${shortcode}}}`;
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const scrollTop = textarea.scrollTop;
      if (mode === "html") {
        setBody(
          (prev) => prev.substring(0, start) + code + prev.substring(end),
        );
      } else {
        setAiPrompt(
          (prev) => prev.substring(0, start) + code + prev.substring(end),
        );
      }
      setTimeout(() => {
        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(start + code.length, start + code.length);
        textarea.scrollTop = scrollTop;
      }, 0);
    } else {
      if (mode === "html") setBody((prev) => prev + code);
      else setAiPrompt((prev) => prev + code);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let finalBody = body;
    if (contentType === "plainText") {
      finalBody = body.replace(/<[^>]*>?/gm, "");
    }
    const isContentValid = mode === "ai" ? aiPrompt.trim() : finalBody.trim();

    // Resolve scheduling decision.
    // For ALL three modes (Instant / Delayed / Schedule) the server expects the
    // cron pipeline: we always persist `scheduleMode === "scheduled"` together
    // with an ISO `scheduledSendAt`. The cron tick (`* * * * *`) is the only
    // delivery path that respects the user-chosen time.
    let resolvedDay = 0;
    let resolvedScheduledSendAt = null;
    let serverScheduleMode = "scheduled";

    if (!sendTime) {
      alert("Please pick a send time.");
      return;
    }
    const next = nextOccurrence(sendTime);
    if (!next) {
      alert("Send time is not a valid HH:MM value.");
      return;
    }

    if (scheduleMode === "instant") {
      resolvedDay = 0; // legacy semantics for the cron main loop
    } else if (scheduleMode === "delayed") {
      if (day === "" || day === null) {
        alert("Please enter the number of days to delay.");
        return;
      }
      const n = Number(day);
      if (!Number.isFinite(n) || n < 1) {
        alert("Days delay must be 1 or more.");
        return;
      }
      resolvedDay = n;
    } else {
      // scheduleMode === "scheduled"
      resolvedDay = -1; // sentinel; cron tick ignores day when scheduleMode=scheduled
    }
    resolvedScheduledSendAt = next.toISOString();

    if (
      !templateName.trim() ||
      !subject.trim() ||
      !isContentValid ||
      !category
    ) {
      alert("All fields are required including category");
      return;
    }

    onSubmit({
      templateName,
      subject,
      body: finalBody,
      contentType,
      day: resolvedDay,
      category,
      enabled,
      mode,
      aiPrompt,
      scheduleMode: serverScheduleMode,
      scheduledSendAt: resolvedScheduledSendAt,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="text-white">
      <div className="row g-4 mb-4">
        <div className="col-md-6">
          <div className="form-floating-custom">
            <label>Template Name</label>
            <input
              type="text"
              placeholder="e.g. Welcome Series - Day 1"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-floating-custom">
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              {categories.map((cat) => (
                <option key={cat} value={cat} className="bg-dark text-white">
                  {getCategoryLabel(cat)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-12">
          <div className="form-floating-custom">
            <label>Subject Line</label>
            <input
              type="text"
              placeholder="e.g. Exciting news for {{user_name}}"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-4 align-items-end justify-content-between mb-4 pb-4 border-bottom border-gray-800">
        <div className="d-flex align-items-end gap-3 flex-wrap">
          <div
            className="form-floating-custom bg-transparent border-0 p-0"
            style={{ minWidth: "280px" }}
          >
            <label className="text-gray-400 small fw-bold text-uppercase tracking-wider mb-2 d-block">
              Schedule
            </label>
            <div className="segmented-control" style={{ minWidth: "100%" }}>
              <input
                type="radio"
                id="scheduleInstant"
                checked={scheduleMode === "instant"}
                onChange={() => {
                  setScheduleMode("instant");
                  setDay("0");
                  // Pre-populate the time picker so the user can just confirm.
                  if (!sendTime) setSendTime(getDefaultSendTime());
                }}
              />
              <label
                htmlFor="scheduleInstant"
                className="small py-2 m-0 d-flex align-items-center justify-content-center text-nowrap"
              >
                Instant
              </label>

              <input
                type="radio"
                id="scheduleDelayed"
                checked={scheduleMode === "delayed"}
                onChange={() => {
                  setScheduleMode("delayed");
                  setDay(day && Number(day) > 0 ? day : "1");
                  // Pre-populate the time picker so the user can pick a time
                  // (effective delay = now + dayCount OR user-picked time, whichever is later).
                  if (!sendTime) setSendTime(getDefaultSendTime());
                }}
              />
              <label
                htmlFor="scheduleDelayed"
                className="small py-2 m-0 d-flex align-items-center justify-content-center text-nowrap"
              >
                Delayed
              </label>

              <input
                type="radio"
                id="scheduleAtTime"
                checked={scheduleMode === "scheduled"}
                onChange={() => {
                  setScheduleMode("scheduled");
                  if (!sendTime) setSendTime(getDefaultSendTime());
                }}
              />
              <label
                htmlFor="scheduleAtTime"
                className="small py-2 m-0 d-flex align-items-center justify-content-center text-nowrap"
              >
                Schedule
              </label>
            </div>
          </div>

          {scheduleMode === "delayed" && (
            <div style={{ maxWidth: "120px" }}>
              <div className="form-floating-custom">
                <label>Days Delay</label>
                <input
                  type="number"
                  placeholder="1"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                  required
                  min="1"
                />
              </div>
            </div>
          )}

          {/*
            Time picker is shown for ALL three modes (Instant / Delayed / Schedule).
            The server cron tick waits for scheduledSendAt (next occurrence of the
            picked time, defaults to "now+10min" if user doesn't change it) to elapse,
            then sends automatically.
            - Instant mode: send at the next occurrence of the picked time.
            - Delayed mode: send at the next occurrence of the picked time after dayCount.
            - Schedule mode: send at the next occurrence of the picked time.
          */}
          <div style={{ minWidth: "180px" }}>
            {/*
              Inline flex container holding:
                - "Send time" label
                - <input type="time" /> (dark-themed +24h clock icon)
                - "⟳ Reset to now + 10 min" button
              All on one row, vertically centered. Wraps to two rows on
              narrow screens via `flex-wrap`.
            */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <label
                className="text-gray-400 fw-medium small mb-0"
                htmlFor="sendTimeInput"
                style={{ whiteSpace: "nowrap" }}
              >
                Send time
              </label>
              <div className="send-time-wrap position-relative d-inline-flex align-items-center">
                <span
                  className="send-time-icon"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "12px", pointerEvents: "none", color: "#94a3b8", fontSize: "14px", zIndex: 1 }}
                >
                  🕒
                </span>
                <input
                  id="sendTimeInput"
                  type="time"
                  className="form-control bg-dark-surface border-gray-700 text-white rounded-pill ps-5 pe-3 py-1 shadow-none focus-ring-primary send-time-input"
                  style={{ width: "auto", minWidth: "150px" }}
                  value={sendTime}
                  onChange={(e) => setSendTime(e.target.value)}
                  required
                />
              </div>
              <button
                type="button"
                className="btn btn-glass btn-sm rounded-pill px-3"
                onClick={() => setSendTime(getDefaultSendTime())}
                title="Reset to now + 10 minutes"
              >
                ⟳ Reset to now + 10 min
              </button>
            </div>
            <div className="text-gray-500 small mt-1">
              <i>
                Email will be sent at this time (next occurrence).
                {sendTime &&
                  (() => {
                    const next = nextOccurrence(sendTime);
                    return next
                      ? ` Next: ${next.toLocaleString()}`
                      : "";
                  })()}
              </i>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-3">
          <span className="text-gray-400 fw-medium small">Status</span>
          <div className="form-check form-switch custom-switch mb-0">
            <input
              className="form-check-input cursor-pointer shadow-none"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </div>
          <span className={enabled ? "text-success fw-bold" : "text-gray-500"}>
            {enabled ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      <div className="d-flex flex-column flex-lg-row gap-4">
        <div className="flex-grow-1" style={{ flex: "1 1 50%" }}>
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-white">Editor Mode</h6>
          <div className="segmented-control" style={{ minWidth: "280px" }}>
            <input
              type="radio"
              id="modeHtml"
              checked={mode === "html"}
              onChange={() => setMode("html")}
            />
            <label
              htmlFor="modeHtml"
              className="d-flex align-items-center justify-content-center gap-2 m-0 text-nowrap"
            >
              <Code size={16} /> HTML
            </label>

            <input
              type="radio"
              id="modeAi"
              checked={mode === "ai"}
              onChange={() => setMode("ai")}
            />
            <label
              htmlFor="modeAi"
              className="d-flex align-items-center justify-content-center gap-2 m-0 text-nowrap"
            >
              <Bot size={16} /> AI Prompt
            </label>
          </div>
        </div>

        {mode === "html" && (
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3 p-3 bg-dark-surface rounded-3 border border-gray-800">
            <span className="text-gray-400 small fw-medium">
              Content Format:
            </span>
            <div className="segmented-control" style={{ minWidth: "280px" }}>
              <input
                type="radio"
                id="htmlAllowed"
                checked={contentType === "htmlAllowed"}
                onChange={() => setContentType("htmlAllowed")}
              />
              <label
                htmlFor="htmlAllowed"
                className="small py-2 m-0 d-flex align-items-center justify-content-center text-nowrap"
              >
                Rich HTML
              </label>

              {/* Plain Text option hidden — only Rich HTML is supported */}
              {false && (
                <>
                  <input
                    type="radio"
                    id="plainText"
                    checked={contentType === "plainText"}
                    onChange={() => setContentType("plainText")}
                  />
                  <label
                    htmlFor="plainText"
                    className="small py-2 m-0 d-flex align-items-center justify-content-center text-nowrap"
                  >
                    Plain Text
                  </label>
                </>
              )}
            </div>
          </div>
        )}

        <div className="form-floating-custom p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label>
              {mode === "html" ? "Message Content" : "AI Generation Prompt"}
            </label>
          </div>
          {mode === "html" ? (
            <textarea
              ref={textareaRef}
              className="custom-scrollbar"
              style={{
                minHeight: "400px",
                maxHeight: "600px",
                overflowY: "auto",
                resize: "vertical",
              }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              placeholder="<h2>Hello {{user_name}}</h2>..."
            />
          ) : (
            <textarea
              ref={textareaRef}
              className="custom-scrollbar"
              style={{
                minHeight: "400px",
                maxHeight: "600px",
                overflowY: "auto",
                resize: "vertical",
              }}
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              required
              placeholder="Generate a welcome email for {{user_name}} from Acme Corp..."
            />
          )}
        </div>
      </div>

      <div className="mb-5">
        <label className="text-gray-400 small fw-bold text-uppercase tracking-wider mb-2 d-block">
          Available Variables (Click to insert)
        </label>
        <div className="d-flex flex-wrap gap-2">
          {availableShortcodes.map((code) => (
            <button
              key={code}
              type="button"
              className="btn btn-sm btn-glass rounded-pill px-3"
              onClick={() => insertShortcode(code)}
            >
              {code}
            </button>
          ))}
          {availableShortcodes.length === 0 && (
            <span className="text-gray-500 small">No variables found</span>
          )}
        </div>
      </div>
      </div>
      
      {/* Live Preview Pane */}
      <div className="d-none d-lg-block" style={{ flex: "1 1 50%", borderLeft: "1px solid #1e293b", paddingLeft: "1.5rem" }}>
        <h6 className="fw-bold mb-3 text-white d-flex align-items-center gap-2">
          <Eye size={18} className="text-info" /> Live Preview
        </h6>
        <div className={`glass-panel rounded-4 custom-scrollbar text-dark ${mode === "html" && contentType !== "plainText" ? "" : "p-4"}`} style={{ height: "calc(100% - 3rem)", minHeight: "400px", maxHeight: "600px", overflowY: "auto", backgroundColor: "#f8f9fa" }}>
          {mode === "html" ? (
            contentType === "plainText" ? (
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "15px", lineHeight: "1.7" }}>
                {body || <span className="text-gray-500">Preview will appear here...</span>}
              </div>
            ) : (
              body ? <div className="email-preview-container" dangerouslySetInnerHTML={{ __html: `<style>
                .email-preview-container { word-wrap: break-word !important; overflow-wrap: break-word !important; word-break: break-word !important; }
                .email-preview-container * { max-width: 100% !important; box-sizing: border-box !important; }
                .email-preview-container img { height: auto !important; }
              </style>` + DOMPurify.sanitize(body) }} style={{ fontSize: "15px", lineHeight: "1.7", overflowWrap: "anywhere" }} /> : <span className="text-gray-500">Preview will appear here...</span>
            )
          ) : (
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }} className="text-gray-600">
              <span className="badge bg-secondary mb-3">AI Prompt Preview</span><br />
              {aiPrompt || "Prompt preview will appear here..."}
            </div>
          )}
        </div>
      </div>
      </div>

      <div className="d-flex justify-content-end gap-3 pt-4 border-top border-gray-800 mt-4">
        <button
          type="button"
          className="btn btn-outline-light rounded-pill px-4 border-opacity-25"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-gradient-glow rounded-pill px-5 fw-bold shadow-lg py-2"
        >
          {editingTemplate ? "Save Changes" : "Create Template"}
        </button>
      </div>
    </form>
  );
};

export default UserEmailTemplate;
